import { addDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { getConverter } from '../../../firebase';
import { createAppAsyncThunk } from '../../../hooks';
import getStytchOrg from '../../../util/getStytchOrgId';
import type { CompData, CompSubString, CompTakeData } from '../../RecordingSessionDataGrid/types';
import { episodeCompByIdRef, episodeCompsRef } from '../util';
import { selectComps } from './selectors';

type addCompParams = {
    sessionId: string
    episodeId: string
    lineId: string
    takeId: string
    subString: CompSubString
}

export const addComp = createAppAsyncThunk(
    'recordingSession/comps/add',
    async ({ sessionId, episodeId, lineId, takeId, subString }: addCompParams, thunkAPI) => {
        const org = getStytchOrg();

        if (!org) return thunkAPI.rejectWithValue('Stytch not initialized or something else went wrong');

        const compConverter = getConverter<CompData>(org);

        const compRef = episodeCompsRef(sessionId, episodeId).withConverter(compConverter);

        const newComp = await addDoc(compRef, {
            takes: [{
                takeId,
                subStrings: [subString],
            }],
            note: '',
            lineId,
            circled: false,
            alt: false,
        });

        return newComp.id;
    }
)

type removeCompParams = {
    sessionId: string
    episodeId: string
    compId: string
}

export const removeComp = createAppAsyncThunk(
    'recordingSession/comps/remove',
    async ({ sessionId, episodeId, compId }: removeCompParams, thunkAPI) => {
        const org = getStytchOrg();

        if (!org) return thunkAPI.rejectWithValue('Stytch not initialized or something else went wrong');

        const compConverter = getConverter<CompData>(org);

        const compRef = episodeCompByIdRef(sessionId, episodeId, compId).withConverter(compConverter);

        await deleteDoc(compRef);
    }
);

type addCompTakeParams = {
    sessionId: string
    episodeId: string
    compId: string
    takeId: string
    subString: CompSubString
}

export const addCompTake = createAppAsyncThunk(
    'recordingSession/comps/addTake',
    async ({ sessionId, episodeId, compId, takeId, subString }: addCompTakeParams, thunkAPI) => {
        const org = getStytchOrg();

        if (!org) return thunkAPI.rejectWithValue('Stytch not initialized or something else went wrong');

        const compConverter = getConverter<CompData>(org);

        const compRef = episodeCompByIdRef(sessionId, episodeId, compId).withConverter(compConverter);

        const state = thunkAPI.getState();
        const comp = selectComps(state).find(c => c.id === compId);

        if (!comp) return thunkAPI.rejectWithValue('Comp not found');

        const existingTake = comp.takes.find(t => t.takeId === takeId);
        if (existingTake) {
            const newSubStrings = [...existingTake.subStrings, subString];

            const newTakes = comp.takes.map(t => {
                if (t.takeId === takeId) {
                    return { ...t, subStrings: newSubStrings };
                }
                return t;
            });

            await updateDoc(compRef, 'takes', newTakes);
        } else {
            const newTakes = [...comp.takes, { takeId, subStrings: [ subString ]} satisfies CompTakeData ];
            await updateDoc(compRef, 'takes', newTakes);
        }
    }
)

type removeCompTakeParams = {
    sessionId: string
    episodeId: string
    compId: string
    takeId: string
}

export const removeCompTake = createAppAsyncThunk(
    'recordingSession/comps/removeTake',
    async ({ sessionId, episodeId, compId, takeId }: removeCompTakeParams, thunkAPI) => {
        const org = getStytchOrg();

        if (!org) return thunkAPI.rejectWithValue('Stytch not initialized or something else went wrong');

        const compConverter = getConverter<CompData>(org);

        const compRef = episodeCompByIdRef(sessionId, episodeId, compId).withConverter(compConverter);

        const state = thunkAPI.getState();
        const comp = selectComps(state).find(c => c.id === compId);

        if (!comp) return thunkAPI.rejectWithValue('Comp not found');

        const newTakes = comp.takes.filter(t => t.takeId !== takeId);

        await updateDoc(compRef, 'takes', newTakes);
    }
)

type circleCompParams = {
    sessionId: string
    episodeId: string
    compId: string
}

export const circleComp = createAppAsyncThunk(
    'recordingSession/comps/circle',
    async ({ compId }: circleCompParams, thunkAPI) => {
        const org = getStytchOrg();

        if (!org) return thunkAPI.rejectWithValue('Stytch not initialized or something else went wrong');

        // const compConverter = getConverter<CompData>(org);

        // const compRef = episodeCompByIdRef(sessionId, episodeId, compId).withConverter(compConverter);

        const state = thunkAPI.getState();
        const comp = selectComps(state).find(c => c.id === compId);

        if (!comp) return thunkAPI.rejectWithValue('Comp not found');

        // const nextVal = getNextCircleAltValue(comp.circled, comp.alt);
        // void updateDoc(compRef, nextVal);
    }
)